import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Checkbox from '@/components/General/inputs/Checkbox';
import Unicon from '@/components/General/Unicon';
import { getPermissionDisplayLabel, PERMISSION_MODULE_LABELS } from '@/utils/permissionLabels';

const CRUD_OPERATIONS = [
    { key: 'READ',   label: 'View',   icon: 'eye-outline' },
    { key: 'CREATE', label: 'Create', icon: 'add-circle-outline' },
    { key: 'UPDATE', label: 'Edit',   icon: 'create-outline' },
    { key: 'DELETE', label: 'Delete', icon: 'trash-outline' },
];

function isCrudPermission(permission) {
    return CRUD_OPERATIONS.some(op => op.key === permission.operation);
}

function ModuleSection({ moduleName, modulePermissions, selectedNames, onPermissionChange, onPermissionBatch, disabled, expanded, onToggle }) {

    const crudPerms = useMemo(
        () => modulePermissions.filter(isCrudPermission),
        [modulePermissions]
    );
    const specialPerms = useMemo(
        () => modulePermissions.filter(p => !isCrudPermission(p)),
        [modulePermissions]
    );

    const allSelected = modulePermissions.every(p => selectedNames.includes(p.name));
    const noneSelected = modulePermissions.every(p => !selectedNames.includes(p.name));
    const someSelected = !allSelected && !noneSelected;

    const handleSelectAll = () => {
        if (disabled) return;
        if (onPermissionBatch) {
            onPermissionBatch(modulePermissions.map(p => p.name), !allSelected);
        } else {
            if (allSelected) {
                modulePermissions.forEach(p => onPermissionChange(p.name, false));
            } else {
                modulePermissions.forEach(p => onPermissionChange(p.name, true));
            }
        }
    };

    const getOperationState = (opKey) => {
        const opPerms = crudPerms.filter(p => p.operation === opKey);
        if (opPerms.length === 0) return 'none';
        const selectedCount = opPerms.filter(p => selectedNames.includes(p.name)).length;
        if (selectedCount === 0) return 'none';
        if (selectedCount === opPerms.length) return 'all';
        return 'some';
    };

    const handleOperationToggle = (opKey) => {
        if (disabled) return;
        const opPerms = crudPerms.filter(p => p.operation === opKey);
        const state = getOperationState(opKey);
        if (onPermissionBatch) {
            onPermissionBatch(opPerms.map(p => p.name), state !== 'all');
        } else {
            opPerms.forEach(p => onPermissionChange(p.name, state !== 'all'));
        }
    };

    return (
        <View style={styles.moduleContainer}>
            <TouchableOpacity
                style={styles.moduleHeader}
                onPress={onToggle}
                activeOpacity={0.7}
            >
                <View style={styles.moduleHeaderLeft}>
                    <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleSelectAll}
                        disabled={disabled}
                        size={18}
                    />
                    <Text style={styles.moduleName}>{PERMISSION_MODULE_LABELS[moduleName] || moduleName}</Text>
                </View>
                <Unicon
                    name={expanded ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    colorVariant="textTertiary"
                />
            </TouchableOpacity>

            {expanded && (
                <View style={styles.moduleBody}>
                    {crudPerms.length > 0 && (
                        <View style={styles.crudRow}>
                            {CRUD_OPERATIONS.map(op => {
                                const state = getOperationState(op.key);
                                const opPerms = crudPerms.filter(p => p.operation === op.key);
                                const hasPermissions = opPerms.length > 0;
                                return (
                                    <Pressable
                                        key={op.key}
                                        style={styles.crudCell(state === 'all', state === 'some', !hasPermissions || disabled)}
                                        onPress={() => handleOperationToggle(op.key)}
                                        disabled={!hasPermissions || disabled}
                                    >
                                        <Unicon
                                            name={op.icon}
                                            size={14}
                                            colorVariant={state === 'all' ? 'onPrimary' : state === 'some' ? 'primary' : 'textTertiary'}
                                        />
                                        <Text style={styles.crudLabel(state === 'all', state === 'some', !hasPermissions)}>{op.label}</Text>
                                        {state === 'some' && (
                                            <View style={styles.partialDot} />
                                        )}
                                    </Pressable>
                                );
                            })}
                        </View>
                    )}

                    {specialPerms.length > 0 && (
                        <View style={styles.specialSection}>
                            {crudPerms.length > 0 && (
                                <View style={styles.specialDivider} />
                            )}
                            {specialPerms.map(perm => {
                                const isSelected = selectedNames.includes(perm.name);
                                return (
                                    <Pressable
                                        key={perm.name}
                                        style={styles.specialRow}
                                        onPress={() => !disabled && onPermissionChange(perm.name, !isSelected)}
                                        disabled={disabled}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={(val) => onPermissionChange(perm.name, val)}
                                            disabled={disabled}
                                            size={18}
                                        />
                                        <View style={styles.specialLabelContainer}>
                                            <Text style={styles.specialLabel}>{getPermissionDisplayLabel(perm.name)}</Text>
                                            {perm.description && (
                                                <Text style={styles.specialDescription}>{perm.description}</Text>
                                            )}
                                        </View>
                                    </Pressable>
                                );
                            })}
                        </View>
                    )}
                </View>
            )}
        </View>
    );
}

const PermissionsEditor = ({ permissions = [], selectedPermissions = [], onPermissionChange, onPermissionBatch, disabled = false }) => {
    const [expandedModules, setExpandedModules] = useState(() => {
        const map = {};
        permissions.forEach(p => {
            if (!map[p.module]) map[p.module] = true;
        });
        return map;
    });

    const modules = useMemo(() => {
        const map = {};
        permissions.forEach(p => {
            if (!map[p.module]) map[p.module] = [];
            map[p.module].push(p);
        });
        return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
    }, [permissions]);

    const selectedNames = useMemo(
        () => selectedPermissions?.map(p => p?.name).filter(Boolean) || [],
        [selectedPermissions]
    );

    const toggleModule = (moduleName) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleName]: !prev[moduleName]
        }));
    };

    const expandAll = () => {
        const map = {};
        modules.forEach(([moduleName]) => {
            map[moduleName] = true;
        });
        setExpandedModules(map);
    };

    const collapseAll = () => {
        const map = {};
        modules.forEach(([moduleName]) => {
            map[moduleName] = false;
        });
        setExpandedModules(map);
    };

    const allExpanded = modules.every(([moduleName]) => expandedModules[moduleName]);
    const allCollapsed = modules.every(([moduleName]) => !expandedModules[moduleName]);

    return (
        <View style={styles.container}>
            <View style={styles.actionBar}>
                <TouchableOpacity
                    onPress={expandAll}
                    disabled={allExpanded}
                    style={[styles.actionButton, allExpanded && styles.actionButtonDisabled]}
                >
                    <Text style={styles.actionButtonText}>Expand All</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    onPress={collapseAll}
                    disabled={allCollapsed}
                    style={[styles.actionButton, allCollapsed && styles.actionButtonDisabled]}
                >
                    <Text style={styles.actionButtonText}>Collapse All</Text>
                </TouchableOpacity>
            </View>
            {modules.map(([moduleName, modulePermissions]) => (
                <ModuleSection
                    key={moduleName}
                    moduleName={moduleName}
                    modulePermissions={modulePermissions}
                    selectedNames={selectedNames}
                    onPermissionChange={onPermissionChange}
                    onPermissionBatch={onPermissionBatch}
                    disabled={disabled}
                    expanded={expandedModules[moduleName]}
                    onToggle={() => toggleModule(moduleName)}
                />
            ))}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows }) => ({
    container: {
        gap: spacing.lg,
        boxShadow: shadows.card,
    },
    actionBar: {
        flexDirection: 'row',
        gap: 8,
        marginBottom: spacing.sm,
    },
    actionButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 6,
        backgroundColor: colors.surfaceVariant,
        borderWidth: 1,
        borderColor: colors.border,
    },
    actionButtonDisabled: {
        opacity: 0.5,
    },
    actionButtonText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.primary,
    },
    moduleContainer: {
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.border,
        overflow: 'hidden',
        backgroundColor: colors.background,
    },
    moduleHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 12,
        paddingHorizontal: 14,
        backgroundColor: colors.surfaceVariant,
        _web: {
            cursor: 'pointer',
        },
    },
    moduleHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    moduleName: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    moduleBody: {
        paddingHorizontal: 14,
        paddingBottom: 10,
        paddingTop: 8,
    },
    crudRow: {
        flexDirection: 'row',
        gap: 6,
    },
    crudCell: (isActive, isPartial, isDisabled) => ({
        width: 100,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        paddingVertical: 7,
        paddingHorizontal: 8,
        borderRadius: 8,
        borderWidth: isActive ? 0 : 1.5,
        borderColor: isPartial ? colors.primary : colors.border,
        backgroundColor: isActive ? colors.primary : isDisabled ? colors.surfaceVariant : colors.surface,
        opacity: isDisabled ? 0.5 : 1,
        _web: {
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease',
        },
    }),
    crudLabel: (isActive, isPartial, isDisabled) => ({
        fontSize: typography.caption,
        fontFamily: isActive || isPartial ? fonts.bold : fonts.body,
        color: isActive ? colors.onPrimary : isDisabled ? colors.textTertiary : isPartial ? colors.primary : colors.textSecondary,
    }),
    partialDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.primary,
        opacity: 0.7,
    },
    specialDivider: {
        height: 1,
        backgroundColor: colors.border,
        marginVertical: 8,
    },
    specialSection: {},
    specialRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        paddingVertical: 8,
        _web: {
            cursor: 'pointer',
        },
    },
    specialLabelContainer: {
        flex: 1,
        paddingTop: 1,
    },
    specialLabel: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
    },
    specialDescription: {
        fontSize: typography.caption,
        color: colors.textTertiary,
        lineHeight: 17,
        marginTop: 2,
    },
}));

export default PermissionsEditor;
